import { GtmEventNames, type DataLayer, type GtmEvent, type GtmVariable } from './types';
import { trackStartEvent } from './event-trigger';
import { trackScroll } from './event-tracking';
import { env } from '$env/dynamic/public';
import { browser } from '$app/environment';

const GTM_TAG_MANAGER_ID = env.PUBLIC_GTM_TAG_MANAGER_ID;

export function getGtmJsUrl() {
  if (!GTM_TAG_MANAGER_ID) {
    return undefined;
  }
  const topLevel = getTopLevelDomain();
  return `https://analytics.stickerapp.${topLevel}/gtm.js?id=${GTM_TAG_MANAGER_ID}&l=dataLayer`;
}

export const shouldLoadGtm = () => {
  return (
    typeof window !== 'undefined' &&
    !!GTM_TAG_MANAGER_ID &&
    new URL(window.location.toString()).searchParams.get('gtm') !== 'off'
  );
};

function getTopLevelDomain() {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  if (hostname === 'localhost') {
    return 'com';
  }

  const multiPartTlds = ['co.uk']; // Add if more needed
  const lastTwo = parts.slice(-2).join('.');
  const lastOne = parts.at(-1);

  if (multiPartTlds.includes(lastTwo)) {
    return lastTwo;
  }

  return lastOne;
}

export function gtmInit() {
  if (!shouldLoadGtm()) {
    return;
  }
  initEvents();
  renderGtmScript();
  renderGtmNoScript();
  trackScroll();
  startListeningToCustomGtmEvents();
}
gtmInit();

function renderGtmScript() {
  const headElement = document.head;
  const gtmScriptTag = document.createElement('script');
  gtmScriptTag.async = true;
  const nonceElement = document.querySelector('[nonce]');
  gtmScriptTag.src = getGtmJsUrl()!;
  const nonce =
    (nonceElement as unknown as { nonce: string | undefined })?.nonce ||
    nonceElement?.getAttribute('nonce');
  if (nonce) {
    gtmScriptTag.setAttribute('nonce', nonce);
  }
  headElement.insertBefore(gtmScriptTag, headElement.firstChild);
}

function renderGtmNoScript() {
  const noScriptTag = document.createElement('noscript');
  const topLevel = getTopLevelDomain();
  const src = `https://analytics.stickerapp.${topLevel}/ns.html?id=${GTM_TAG_MANAGER_ID}`;

  noScriptTag.innerHTML = `<iframe
      src="${src}"
      height="0"
      width="0"
      style="display:none;visibility:hidden"
    ></iframe>`;
  document.body.insertBefore(noScriptTag, document.body.firstChild);
}

function initEvents() {
  // Check if any event is of type GtmEventNames.Start
  const hasGtmStart = window.dataLayer?.some(
    (gtmEvent) => (gtmEvent as GtmEvent)?.event === GtmEventNames.Start,
  );
  // If there's no GTM start event, track the start event
  if (!hasGtmStart) {
    trackStartEvent();
  }
}

function clearDataLayer<T extends GtmEvent>(event: T, dataLayer: DataLayer) {
  if ('event_params' in event) {
    dataLayer.push({ event_params: null });
  }

  if ('ecommerce' in event) {
    dataLayer.push({ ecommerce: null });
  }
}

export function pushEvent<T extends GtmEvent>(event: T) {
  if (browser) {
    window.dataLayer = window.dataLayer || [];
    clearDataLayer(event, window.dataLayer);
    window.dataLayer.push(event);
  }
}

export function pushVariable<T extends GtmVariable>(variable: T) {
  if (browser) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(variable);
  }
}

function startListeningToCustomGtmEvents() {
  document.addEventListener('gtm-event', (event) => {
    const gtmEvent = (event as CustomEvent).detail;
    pushEvent(gtmEvent);
  });
}
